import React, { useEffect, useRef, useState } from 'react';
import Phaser from 'phaser';
import './SpaceInvadersGame.css';

interface SpaceInvadersGameProps {
  isRunning: boolean;
}

class SpaceInvadersScene extends Phaser.Scene {
  private player!: Phaser.Physics.Arcade.Sprite;
  private enemies!: Phaser.Physics.Arcade.Group;
  private bosses!: Phaser.Physics.Arcade.Group;
  private bullets!: Phaser.Physics.Arcade.Group;
  private cursors!: Phaser.Types.Input.Keyboard.CursorKeys;
  private fireKey!: Phaser.Input.Keyboard.Key;
  private lastFired: number = 0;
  private scoreText!: Phaser.GameObjects.Text;
  private waveText!: Phaser.GameObjects.Text;
  private gameOverText!: Phaser.GameObjects.Text;
  private restartButton!: Phaser.GameObjects.Text;
  private score: number = 0;
  private enemySpeed: number = 30;
  private enemyDirection: number = 1;
  private currentWave: number = 1;
  private gameOver: boolean = false;
  private isWaveTransitioning: boolean = false;
  private onScoreChange: (score: number) => void;
  private bossHealth: Map<number, number> = new Map();

  constructor(onScoreChange: (score: number) => void) {
    super({ key: 'SpaceInvadersScene' });
    this.onScoreChange = onScoreChange;
  }

  preload() {
    // Create better sprites using vector graphics
    this.createPlayerTexture();
    this.createEnemyTextures();
    this.createBulletTexture();
  }

  createPlayerTexture() {
    // Create a more detailed spaceship
    const graphics = this.make.graphics({ x: 0, y: 0 });
    
    // Ship body
    graphics.fillStyle(0x457b9d);
    graphics.fillRect(10, 5, 30, 15);
    
    // Ship cockpit
    graphics.fillStyle(0x90e0ef);
    graphics.fillRect(25, 2, 10, 5);
    
    // Ship wings
    graphics.fillStyle(0x023e8a);
    graphics.beginPath();
    graphics.moveTo(5, 20);
    graphics.lineTo(15, 10);
    graphics.lineTo(35, 10);
    graphics.lineTo(45, 20);
    graphics.closePath();
    graphics.fill();
    
    // Ship thrusters
    graphics.fillStyle(0xe63946);
    graphics.fillRect(15, 20, 5, 3);
    graphics.fillRect(30, 20, 5, 3);
    
    graphics.generateTexture('player', 50, 25);
  }

  createEnemyTextures() {
    // Regular enemy (small alien)
    const enemyGraphics = this.make.graphics({ x: 0, y: 0 });
    
    // Alien body
    enemyGraphics.fillStyle(0x2a9d8f);
    enemyGraphics.fillRect(5, 5, 20, 15);
    
    // Alien eyes
    enemyGraphics.fillStyle(0xffb703);
    enemyGraphics.fillRect(8, 8, 4, 4);
    enemyGraphics.fillRect(18, 8, 4, 4);
    
    // Alien tentacles
    enemyGraphics.fillStyle(0x2a9d8f);
    enemyGraphics.fillRect(8, 20, 3, 5);
    enemyGraphics.fillRect(14, 20, 3, 5);
    enemyGraphics.fillRect(20, 20, 3, 5);
    
    enemyGraphics.generateTexture('enemy', 30, 25);
    
    // Boss alien (bigger with different design)
    const bossGraphics = this.make.graphics({ x: 0, y: 0 });
    
    // Boss body
    bossGraphics.fillStyle(0xf94144);
    bossGraphics.fillRect(10, 10, 40, 25);
    
    // Boss crown
    bossGraphics.fillStyle(0xf8961e);
    bossGraphics.fillTriangle(15, 10, 30, 0, 45, 10);
    
    // Boss eyes
    bossGraphics.fillStyle(0x90e0ef);
    bossGraphics.fillRect(18, 20, 8, 8);
    bossGraphics.fillRect(34, 20, 8, 8);
    
    // Boss mouth
    bossGraphics.fillStyle(0x000000);
    bossGraphics.fillRect(25, 30, 10, 5);
    
    // Boss tentacles
    bossGraphics.fillStyle(0xf94144);
    bossGraphics.fillRect(15, 35, 5, 10);
    bossGraphics.fillRect(25, 35, 5, 10);
    bossGraphics.fillRect(35, 35, 5, 10);
    
    bossGraphics.generateTexture('boss', 60, 45);
  }

  createBulletTexture() {
    const graphics = this.make.graphics({ x: 0, y: 0 });
    
    // Laser bullet
    graphics.fillStyle(0xe63946);
    graphics.fillRect(2, 0, 4, 15);
    
    // Bullet glow
    graphics.fillStyle(0xffb703);
    graphics.fillRect(3, 15, 2, 3);
    
    graphics.generateTexture('bullet', 8, 18);
  }

  create() {
    this.initGame();
    
    // Add R key for restart
    this.input.keyboard!.on('keydown-R', () => {
      if (this.gameOver) {
        this.initGame();
      }
    });
  }

  initGame() {
    // Reset game state
    this.score = 0;
    this.gameOver = false;
    this.enemySpeed = 30;
    this.enemyDirection = 1;
    this.currentWave = 1;
    this.isWaveTransitioning = false;
    this.bossHealth.clear();
    
    // Clear any existing game objects
    this.children.removeAll();
    
    // Create player
    this.player = this.physics.add.sprite(200, 280, 'player');
    this.player.setCollideWorldBounds(true);
    this.player.setScale(0.8);

    // Create bullets
    this.bullets = this.physics.add.group({
      defaultKey: 'bullet',
      maxSize: 10
    });

    // Create enemies
    this.enemies = this.physics.add.group();
    this.bosses = this.physics.add.group();
    this.createEnemies();

    // Set up collision detection for regular enemies
    this.physics.add.collider(
      this.bullets, 
      this.enemies, 
      (bullet, enemy) => {
        this.bulletHitEnemy(
          bullet as Phaser.GameObjects.GameObject, 
          enemy as Phaser.GameObjects.GameObject
        );
      }, 
      undefined, 
      this
    );
    
    // Set up collision detection for boss enemies
    this.physics.add.collider(
      this.bullets, 
      this.bosses, 
      (bullet, boss) => {
        this.bulletHitBoss(
          bullet as Phaser.GameObjects.GameObject, 
          boss as Phaser.GameObjects.GameObject
        );
      }, 
      undefined, 
      this
    );

    // Set up controls
    this.cursors = this.input.keyboard!.createCursorKeys();
    this.fireKey = this.input.keyboard!.addKey(Phaser.Input.Keyboard.KeyCodes.SPACE);

    // Add score text
    this.scoreText = this.add.text(16, 16, 'Score: 0', { 
      fontSize: '16px', 
      color: '#fff',
      fontFamily: 'Arial'
    });
    
    // Add wave text
    this.waveText = this.add.text(200, 16, 'Wave: 1', { 
      fontSize: '16px', 
      color: '#fff',
      fontFamily: 'Arial'
    });
    this.waveText.setOrigin(0.5, 0);
    
    // Initialize game over text (hidden initially)
    this.gameOverText = this.add.text(200, 150, 'GAME OVER', {
      fontSize: '32px',
      color: '#e63946',
      fontFamily: 'Arial',
      fontStyle: 'bold'
    });
    this.gameOverText.setOrigin(0.5);
    this.gameOverText.setVisible(false);
    
    // Initialize restart button (hidden initially)
    this.restartButton = this.add.text(200, 200, 'RESTART', {
      fontSize: '24px',
      color: '#fff',
      backgroundColor: '#457b9d',
      fontFamily: 'Arial',
      padding: { x: 10, y: 5 }
    });
    this.restartButton.setOrigin(0.5);
    this.restartButton.setInteractive({ useHandCursor: true });
    this.restartButton.on('pointerdown', () => this.initGame());
    this.restartButton.on('pointerover', () => this.restartButton.setStyle({ color: '#ffd166' }));
    this.restartButton.on('pointerout', () => this.restartButton.setStyle({ color: '#fff' }));
    this.restartButton.setVisible(false);
  }

  createEnemies() {
    // Clear existing enemies
    this.enemies.clear(true, true);
    this.bosses.clear(true, true);
    
    const rows = Math.min(3, this.currentWave);
    const cols = 8;
    const enemyWidth = 30;
    const enemyHeight = 25;
    const padding = 15;
    const offsetTop = 40;
    const offsetLeft = (400 - ((enemyWidth + padding) * cols)) / 2;

    // Create regular enemies
    for (let row = 0; row < rows; row++) {
      for (let col = 0; col < cols; col++) {
        const x = offsetLeft + col * (enemyWidth + padding);
        const y = offsetTop + row * (enemyHeight + padding);
        const enemy = this.enemies.create(x, y, 'enemy');
        enemy.setOrigin(0);
      }
    }
    
    // Create boss enemies after wave 1
    if (this.currentWave > 1) {
      const bossCount = Math.min(Math.ceil((this.currentWave - 1) / 2), 3); // Slower boss progression: 1 boss at wave 2-3, 2 bosses at wave 4-5, 3 bosses at wave 6+
      const bossWidth = 60;
      const bossSpacing = (400 - (bossCount * bossWidth)) / (bossCount + 1);
      
      for (let i = 0; i < bossCount; i++) {
        const x = bossSpacing + i * (bossWidth + bossSpacing);
        const y = 10;
        const boss = this.bosses.create(x, y, 'boss');
        boss.setOrigin(0);
        boss.setData('id', Date.now() + i); // Unique ID for tracking health
        
        // Add collision with world bounds to prevent escaping
        boss.setCollideWorldBounds(true);
        
        // More gradual health scaling: 1 hit for first boss, +1 every 2 waves
        const health = Math.min(1 + Math.floor(this.currentWave / 2), 4); // Max 4 health
        this.bossHealth.set(boss.getData('id'), health);
      }
    }
  }

  showGameOver() {
    this.gameOver = true;
    this.gameOverText.setVisible(true);
    this.restartButton.setVisible(true);
    
    // Stop player and enemies
    this.player.setVelocity(0);
    this.enemies.getChildren().forEach((enemy: Phaser.GameObjects.GameObject) => {
      const e = enemy as Phaser.Physics.Arcade.Sprite;
      e.setVelocity(0);
    });
    this.bosses.getChildren().forEach((boss: Phaser.GameObjects.GameObject) => {
      const b = boss as Phaser.Physics.Arcade.Sprite;
      b.setVelocity(0);
    });
  }

  startNextWave() {
    if (this.isWaveTransitioning) return;
    
    this.isWaveTransitioning = true;
    this.currentWave++;
    
    // Animate wave text
    this.waveText.setText(`Wave: ${this.currentWave}`);
    this.waveText.setScale(1);
    this.tweens.add({
      targets: this.waveText,
      scale: 1.5,
      duration: 300,
      yoyo: true,
      ease: 'Sine.easeInOut',
      onComplete: () => {
        this.waveText.setScale(1);
      }
    });
    
    // Show wave transition message
    const waveMessage = this.add.text(200, 150, `WAVE ${this.currentWave}`, {
      fontSize: '36px',
      fontStyle: 'bold',
      color: '#ffffff',
      stroke: '#000000',
      strokeThickness: 4,
      fontFamily: 'Arial'
    });
    waveMessage.setOrigin(0.5);
    
    // Animate wave message
    this.tweens.add({
      targets: waveMessage,
      alpha: { from: 0, to: 1 },
      scale: { from: 2, to: 1 },
      duration: 800,
      ease: 'Power2',
      yoyo: true,
      hold: 600,
      onComplete: () => {
        waveMessage.destroy();
        
        // More gradual speed increase (5 instead of 10)
        this.enemySpeed += 5;
        
        // Create new enemies after animation completes
        this.createEnemies();
        
        // Apply glow effect to boss aliens
        this.bosses.getChildren().forEach((boss: Phaser.GameObjects.GameObject) => {
          const b = boss as Phaser.Physics.Arcade.Sprite;
          b.setData('glowEffect', true);
          
          // Add pulsing glow effect
          this.tweens.add({
            targets: b,
            alpha: 0.8,
            duration: 500,
            yoyo: true,
            repeat: -1
          });
        });
        
        // Reset transition flag after enemies are created
        this.isWaveTransitioning = false;
      }
    });
  }

  update(time: number) {
    if (this.gameOver || this.isWaveTransitioning) return;

    // Player movement - increased speed from 200 to 250
    if (this.cursors.left.isDown) {
      this.player.setVelocityX(-250);
    } else if (this.cursors.right.isDown) {
      this.player.setVelocityX(250);
    } else {
      this.player.setVelocityX(0);
    }

    // Firing - reduced cooldown from 250 to 200ms for better responsiveness
    if (this.fireKey.isDown && time > this.lastFired) {
      const bullet = this.bullets.get(this.player.x, this.player.y - 15);
      
      if (bullet) {
        bullet.setActive(true);
        bullet.setVisible(true);
        bullet.setVelocityY(-300);
        this.lastFired = time + 200; // Reduced cooldown between shots
      }
    }

    // Clean up bullets
    this.bullets.getChildren().forEach((bullet: Phaser.GameObjects.GameObject) => {
      const b = bullet as Phaser.Physics.Arcade.Sprite;
      if (b.y < -10) {
        b.setActive(false);
        b.setVisible(false);
      }
    });

    // Move enemies
    if (this.enemies.getLength() > 0 || this.bosses.getLength() > 0) {
      let moveDown = false;
      
      // Move regular enemies - slowed down movement
      this.enemies.getChildren().forEach((enemy: Phaser.GameObjects.GameObject) => {
        const e = enemy as Phaser.Physics.Arcade.Sprite;
        e.x += this.enemySpeed * this.enemyDirection * (1/90); // Reduced from 1/60 to 1/90
        
        // Check if any enemy hits the edges
        if ((e.x < 10 && this.enemyDirection < 0) || 
            (e.x > 390 - e.width && this.enemyDirection > 0)) {
          moveDown = true;
        }
        
        // Check if enemies reach the bottom
        if (e.y > 250) {
          this.showGameOver();
        }
        
        // Ensure enemies stay within bounds
        if (e.x < 0) e.x = 0;
        if (e.x > 400 - e.width) e.x = 400 - e.width;
      });
      
      // Move boss enemies (slower) - further reduced movement
      this.bosses.getChildren().forEach((boss: Phaser.GameObjects.GameObject) => {
        const b = boss as Phaser.Physics.Arcade.Sprite;
        b.x += (this.enemySpeed * 0.5) * this.enemyDirection * (1/90); // Reduced from 0.7 and 1/60 to 0.5 and 1/90
        
        // Check if any boss hits the edges
        if ((b.x < 10 && this.enemyDirection < 0) || 
            (b.x > 390 - b.width && this.enemyDirection > 0)) {
          moveDown = true;
        }
        
        // Check if bosses reach the bottom
        if (b.y > 200) {
          this.showGameOver();
        }
      });
      
      if (moveDown) {
        this.enemyDirection *= -1;
        
        // Move regular enemies down - reduced vertical movement
        this.enemies.getChildren().forEach((enemy: Phaser.GameObjects.GameObject) => {
          const e = enemy as Phaser.Physics.Arcade.Sprite;
          e.y += 10; // Reduced from 15 to 10
        });
        
        // Move boss enemies down - reduced vertical movement
        this.bosses.getChildren().forEach((boss: Phaser.GameObjects.GameObject) => {
          const b = boss as Phaser.Physics.Arcade.Sprite;
          b.y += 7; // Reduced from 10 to 7
        });
      }
      
      // Check if all enemies are destroyed or off-screen
      const areAllEnemiesGone = this.checkAllEnemiesGone();
      if (areAllEnemiesGone && !this.gameOver && !this.isWaveTransitioning) {
        // All enemies destroyed, start next wave
        this.startNextWave();
      }
    } else if (!this.gameOver && !this.isWaveTransitioning) {
      // All enemies destroyed, start next wave
      this.startNextWave();
    }
  }
  
  // Check if all enemies are gone (destroyed or off-screen)
  checkAllEnemiesGone() {
    // If there are active enemies or bosses on screen, return false
    let activeEnemies = 0;
    
    this.enemies.getChildren().forEach((enemy: Phaser.GameObjects.GameObject) => {
      const e = enemy as Phaser.Physics.Arcade.Sprite;
      if (e.active && e.visible && e.y < 300) {
        activeEnemies++;
      }
    });
    
    this.bosses.getChildren().forEach((boss: Phaser.GameObjects.GameObject) => {
      const b = boss as Phaser.Physics.Arcade.Sprite;
      if (b.active && b.visible && b.y < 300) {
        activeEnemies++;
      }
    });
    
    return activeEnemies === 0;
  }

  bulletHitEnemy(bullet: Phaser.GameObjects.GameObject, enemy: Phaser.GameObjects.GameObject) {
    const b = bullet as Phaser.Physics.Arcade.Sprite;
    const e = enemy as Phaser.Physics.Arcade.Sprite;
    
    b.setActive(false);
    b.setVisible(false);
    e.destroy();
    
    this.score += 10;
    this.scoreText.setText(`Score: ${this.score}`);
    this.onScoreChange(this.score);
  }
  
  bulletHitBoss(bullet: Phaser.GameObjects.GameObject, boss: Phaser.GameObjects.GameObject) {
    const b = bullet as Phaser.Physics.Arcade.Sprite;
    const bossSprite = boss as Phaser.Physics.Arcade.Sprite;
    
    b.setActive(false);
    b.setVisible(false);
    
    const bossId = bossSprite.getData('id');
    let health = this.bossHealth.get(bossId) || 0;
    health--;
    
    // Flash the boss to indicate it was hit
    this.tweens.add({
      targets: bossSprite,
      alpha: 0.5,
      duration: 50,
      yoyo: true
    });
    
    if (health <= 0) {
      // Boss destroyed
      bossSprite.destroy();
      this.bossHealth.delete(bossId);
      
      // Higher score for destroying a boss
      this.score += 50;
    } else {
      // Update boss health
      this.bossHealth.set(bossId, health);
      
      // Points for hitting a boss
      this.score += 5;
    }
    
    this.scoreText.setText(`Score: ${this.score}`);
    this.onScoreChange(this.score);
  }
}

const SpaceInvadersGame: React.FC<SpaceInvadersGameProps> = ({ isRunning }) => {
  const gameRef = useRef<HTMLDivElement>(null);
  const [game, setGame] = useState<Phaser.Game | null>(null);
  const [score, setScore] = useState(0);
  
  useEffect(() => {
    if (!isRunning || !gameRef.current) return;
    
    // Game configuration
    const config: Phaser.Types.Core.GameConfig = {
      type: Phaser.AUTO,
      parent: gameRef.current,
      width: 400,
      height: 300,
      backgroundColor: '#000',
      physics: {
        default: 'arcade',
        arcade: {
          gravity: { x: 0, y: 0 },
          debug: false
        }
      },
      scene: [new SpaceInvadersScene(setScore)]
    };
    
    // Create game instance
    const gameInstance = new Phaser.Game(config);
    setGame(gameInstance);
    
    // Clean up
    return () => {
      if (gameInstance) {
        gameInstance.destroy(true);
        setGame(null);
      }
    };
  }, [isRunning]);
  
  return (
    <div className="space-invaders-game">
      <div className="game-container">
        <h3>Admin Special: Space Invaders</h3>
        <p>Use ← → arrows to move, Space to shoot<br />Press R or click RESTART to play again</p>
        <div ref={gameRef} className="game-canvas-container" />
        <div className="game-score">Score: {score}</div>
      </div>
    </div>
  );
};

export default SpaceInvadersGame; 